import { useEffect, useMemo, useRef, useState } from 'react'

import { isSmbMVP } from '@ecomm/data-env-variables'
import { useLocale } from '@ecomm/data-hooks'
import { useAreExpertsAvailable } from '@ecomm/scout'
import { useCartQuantity } from '@ecomm/shared-hooks'
import { useOnKeyUp } from '@ecomm/shared-hooks'
import { useCameraOnlyLPCTAExperience } from '@ecomm/shared-ninetailed'
import { useBusiness } from '@ecomm/smb-components/BusinessProvider/BusinessProvider'
import type { Locale } from '@simplisafe/ewok'
import { useCallback } from 'react'
import { useIsAIChatEnabled } from '../../hooks/useIsAIChatEnabled'
import { useTrackingFullHeader } from '../../hooks/useTrackingFullHeader'
import { SMBHeaderToggle } from './SMBHeaderToggle'
import { SideNav } from './SideNav'
import { TopNav } from './TopNav'
import { getCourtesyNavItems } from './data'

import type { HeaderProps } from './types'

type ExpertHours = {
  readonly init: number
  readonly end: number
  readonly days?: readonly number[]
}

const residentialExpertAvailableHours: Record<Locale, ExpertHours> = {
  'en-US': {
    init: 8,
    end: 22
  },
  'en-GB': {
    init: 9,
    end: 19
  }
}

const businessExpertAvailableHours: Record<Locale, ExpertHours> = {
  'en-US': {
    init: 8,
    end: 20,
    days: [1, 2, 3, 4, 5]
  },
  'en-GB': {
    init: 9,
    end: 19
  }
}

const useGetCourtesyNavItems = () => {
  const locale = useLocale()
  const { isBusiness } = useBusiness()
  const expertsAvailableRange = isBusiness
    ? businessExpertAvailableHours[locale]
    : residentialExpertAvailableHours[locale]

  const areExpertsAvailable = useAreExpertsAvailable(
    expertsAvailableRange.init,
    expertsAvailableRange.end,
    expertsAvailableRange.days
  )
  const { isCameraOnlyVariant } = useCameraOnlyLPCTAExperience()

  const isAIChatEnabled = useIsAIChatEnabled()
  return useMemo(
    () =>
      getCourtesyNavItems(
        locale,
        areExpertsAvailable,
        isAIChatEnabled,
        isCameraOnlyVariant,
        isBusiness
      ),
    [
      locale,
      areExpertsAvailable,
      isAIChatEnabled,
      isCameraOnlyVariant,
      isBusiness
    ]
  )
}

const useNavigationControls = () => {
  const [showSideNav, setShowSideNav] = useState(false)
  const trackFullHeader = useTrackingFullHeader()

  const toggleSideNav = useCallback(
    (track = false) => {
      trackFullHeader(track, showSideNav)
      setShowSideNav(prev => !prev)
    },
    [showSideNav, trackFullHeader]
  )

  const closeSideNav = useCallback(() => {
    setShowSideNav(false)
  }, [setShowSideNav])

  useOnKeyUp('Escape', () => {
    setShowSideNav(false)
  })

  return {
    showSideNav,
    toggleSideNav,
    closeSideNav
  }
}
const useHeaderTop = (headerRef: React.RefObject<HTMLDivElement>) => {
  const [top, setTop] = useState(0)
  useEffect(() => {
    headerRef?.current && setTop(headerRef.current.offsetTop)
  }, [headerRef])
  return top
}

type FullHeaderProps = HeaderProps & {
  readonly id?: string
}

export function FullHeader({ images, quoteWizard }: FullHeaderProps) {
  const locale = useLocale()
  const { isBusiness } = useBusiness()

  const expertsAvailableRange = isBusiness
    ? businessExpertAvailableHours[locale]
    : residentialExpertAvailableHours[locale]

  const areExpertsAvailable = useAreExpertsAvailable(
    expertsAvailableRange.init,
    expertsAvailableRange.end,
    expertsAvailableRange.days
  )
  const headerRef = useRef<HTMLDivElement>(null)
  const top = useHeaderTop(headerRef)
  const { cartQuantity } = useCartQuantity()
  const courtesyNavItems = useGetCourtesyNavItems()
  const { showSideNav, toggleSideNav, closeSideNav } = useNavigationControls()

  return (
    <header
      className="bg-neutral-black relative w-full text-inherit lg:z-20"
      ref={headerRef}
    >
      {isSmbMVP ? (
        <div className="max-[1126px]:hidden ">
          <SMBHeaderToggle />
        </div>
      ) : null}
      <TopNav
        toggleSideNav={toggleSideNav}
        showSideNav={showSideNav}
        images={images}
        quoteWizard={quoteWizard}
        cartQuantity={cartQuantity}
        courtesyNavItems={courtesyNavItems}
      />
      <SideNav
        areExpertsAvailable={areExpertsAvailable}
        close={closeSideNav}
        images={images}
        quoteWizard={quoteWizard}
        show={showSideNav}
        toggle={toggleSideNav}
        top={top}
      />
    </header>
  )
}
